//dependencies
import { ObjectId } from "bson";
import { nanoid } from "nanoid/non-secure";
import React, { ReactNode, useMemo } from "react";

import { faInfoCircle } from "@performyard/fontawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";
import { InputWrapper, Text } from "@performyard/py-components";
//components
import { FontWeight, TextSize } from "@performyard/py-components/src/theme";

import Tooltip from "../../../Tooltip";

export type RatingScaleOption = {
	value: number | string | null;
	text: string;
	description?: string;
};

type RatingScaleInputProps = {
	/**
	 * The value of the input
	 */
	value?: RatingScaleOption["value"];
	/**
	 * The options of the input
	 */
	options: RatingScaleOption[];
	/**
	 * The function to call when the input value changes
	 */
	onChange: (value: RatingScaleOption["value"]) => void;
	/**
	 * The label of the input
	 */
	label?: string;
	/**
	 * size of the label
	 */
	labelSize?: TextSize;
	/**
	 * weight of the label
	 */
	labelWeight?: FontWeight;
	/**
	 * subtitle
	 */
	subtitle?: string | ReactNode;
	/**
	 * The error message of the input
	 */
	errorMessage?: string;
	/**
	 * The class name of the input
	 */
	className?: string;
	/**
	 * testid
	 */
	testid?: string;
};

/**
 * Rating scale input component
 *
 * This component allows inputting answers to a rating scale type question. These allow one of a group of options to be selected, and
 * are treated semantically as radio buttons. Each option includes a value, a text and an optional description. The value is the value
 * used to identify the rating scale option, text is displayed within the selectable option and the description (if provided) is displayed
 * in a tooltip when hovering over the 'i' icon.
 *
 * Label, error message, and subtitle support is included, which will wrap the collection of options in a labelled input group.
 */
const RatingScaleInput = ({
	value,
	options,
	onChange,
	label,
	labelSize = "paragraph-1",
	labelWeight = "semi-bold",
	subtitle = null,
	errorMessage,
	className = "",
	testid = "rating-scale-input",
}: RatingScaleInputProps) => {
	const groupId = useMemo(() => nanoid(), []);

	const parseValue = (value: string | null): number | string | null => {
		if (ObjectId.isValid(value ?? "")) {
			return value;
		} else if (isNaN(parseFloat(value ?? ""))) {
			return value;
		}
		return parseFloat(value ?? "");
	};

	const anyDescriptions = options.some((option) => option.description);

	return (
		<InputWrapper label={label} labelSize={labelSize} labelWeight={labelWeight} error={errorMessage} id={groupId}>
			{subtitle}
			<div
				role="radiogroup"
				aria-labelledby={label ? `label-${groupId}` : undefined}
				data-testid={testid}
				className={`align-start flex flex-col gap-2 ${className}`}
			>
				{options.map((option, index) => {
					const isChecked = value === option.value;
					return (
						<label htmlFor={`${option.value}-${index}`} key={option.value} className="flex h-[32px] gap-2">
							<div
								className={`flex h-full w-full min-w-0 items-center rounded px-2 ${isChecked ? "bg-primary-5 text-neutral-0" : "cursor-pointer bg-neutral-1 hover:bg-neutral-2"}`}
							>
								<input
									id={`${option.value}-${index}`}
									type="radio"
									value={option.value ?? ""}
									style={{ display: "none" }}
									onChange={(e) => {
										const parsedValue = parseValue(e.target.value);
										onChange(e.target.value.length > 0 ? parsedValue : null);
									}}
									checked={isChecked}
									aria-label={option.text}
									aria-checked={isChecked}
								/>
								<Text
									wrap={false}
									size="paragraph-3"
									weight="regular"
									dangerouslySetClassName="py-text-truncate min-w-0"
								>
									{option.text}
								</Text>
							</div>
							{anyDescriptions && (
								<div className="flex h-full w-4 items-center justify-center">
									{option.description && (
										<Tooltip message={option.description}>
											<div className="text-neutral-3 hover:text-neutral-5">
												<FontAwesomeIcon icon={faInfoCircle} />
											</div>
										</Tooltip>
									)}
								</div>
							)}
						</label>
					);
				})}
			</div>
		</InputWrapper>
	);
};

export default RatingScaleInput;
